import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

/**
 * Live outage state for the header indicator, served by the API's
 * `GET /status/outage` (which proxies + caches the public status page). The
 * response is intentionally slim — just enough to decide whether to render the
 * indicator and what to say. The API fails silent (always 200, `outage:false`
 * on any upstream problem), so the hook never needs to distinguish "status page
 * down" from "all clear".
 */

export type OverallStatus =
  | 'operational'
  | 'degraded'
  | 'partial_outage'
  | 'major_outage'
  | 'maintenance';

export interface OutageIncident {
  id: string;
  title: string;
  severity: 'minor' | 'major' | 'critical';
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved';
}

export interface OutageStatus {
  ok: boolean;
  outage: boolean;
  overall: OverallStatus;
  label: string;
  incidents: OutageIncident[];
}

async function fetchOutageStatus(): Promise<OutageStatus> {
  const res = await fetch(`${API_URL}/status/outage`, { credentials: 'include' });
  if (!res.ok) throw await toApiError(res, 'Failed to fetch status');
  return res.json();
}

export function useOutageStatus() {
  return useQuery({
    queryKey: ['outage-status'],
    queryFn: fetchOutageStatus,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
    // The header indicator is non-critical; on error we simply render nothing
    // rather than retrying/spamming.
    retry: false,
  });
}
